import { useState, useEffect, type FormEvent } from 'react';
import { submitForm, createPaymentIntent, createDirectDebitFlow, ApiError, type PaymentIntentResult } from '../api';
import { validateForm } from '../validation';
import { navigateTo } from '../navigation';
import {
  availableMethods,
  resolveFrequency,
  resolveOfferedMethods,
  resolveSelectedMethod,
  type PaymentSelection,
} from '../payment-methods';
import type { PublicForm } from '../types';
import { TextField } from './fields/TextField';
import { EmailField } from './fields/EmailField';
import { PhoneField } from './fields/PhoneField';
import { TextareaField } from './fields/TextareaField';
import { AmountField } from './fields/AmountField';
import { CheckboxField } from './fields/CheckboxField';
import { DropdownField } from './fields/DropdownField';
import { AddressField } from './fields/AddressField';
import { DateField } from './fields/DateField';
import { HiddenField } from './fields/HiddenField';
import { PaymentField } from './fields/PaymentField';
import { ParagraphField } from './fields/ParagraphField';
import { PaymentSection, type PaymentOutcome } from './PaymentSection';
import { ThankYouScreen } from './ThankYouScreen';
import { ErrorScreen } from './ErrorScreen';

const FIELD_COMPONENTS = {
  text: TextField, email: EmailField, phone: PhoneField, textarea: TextareaField,
  amount: AmountField, checkbox: CheckboxField, dropdown: DropdownField,
  address: AddressField, date: DateField, hidden: HiddenField, payment: PaymentField,
  paragraph: ParagraphField,
} as const;

type Status = 'idle' | 'submitting' | 'paying' | 'processing' | 'success' | 'error';

/**
 * What a supporter sees when the Direct Debit sign-up cannot start, keyed by the
 * API's error code rather than its message — see `ApiError`. The point is that
 * none of these may look like a dead button: each says whether trying again is
 * worth it, and points at the other payment method where there is one.
 */
const DIRECT_DEBIT_ERRORS: Record<string, string> = {
  // 409 — the charity has not connected GoCardless, or has connected two and we
  // will not guess which account the mandate belongs under. Retrying is futile.
  NO_GOCARDLESS_CONNECTION:
    'This organisation cannot set up Direct Debits at the moment. Please choose another payment method, or contact them directly.',
  MULTIPLE_GOCARDLESS_CONNECTIONS:
    'This organisation cannot set up Direct Debits at the moment. Please choose another payment method, or contact them directly.',
  // 400 — the form does not collect an email address, which the mandate needs.
  MISSING_CONTACT_EMAIL:
    'This form cannot set up a Direct Debit because it does not collect an email address. Please choose another payment method.',
  // 400 — Direct Debit needs a regular gift; a one-off is a different product.
  DIRECT_DEBIT_REQUIRES_RECURRING:
    'Direct Debit is only available for a regular gift. Please choose a regular amount, or pay by card.',
};

const DIRECT_DEBIT_FALLBACK_ERROR =
  'We could not start the Direct Debit sign-up. Please try again in a few minutes, or choose another payment method.';

function directDebitErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code && DIRECT_DEBIT_ERRORS[err.code]) return DIRECT_DEBIT_ERRORS[err.code];
  // A 5xx message is sanitised in production, so never surface it; a 4xx we do
  // not recognise is a validation message worth showing verbatim.
  if (err instanceof ApiError && err.status < 500 && err.message) return err.message;
  return DIRECT_DEBIT_FALLBACK_ERROR;
}

interface Props {
  form: PublicForm;
  embedKey: string;
  /**
   * Resolves the tenant for the submit and payment-intent calls below (ADR-009).
   * REQUIRED for any real (non-preview) render: App.tsx guards this by never
   * mounting FormRenderer without a tenant in the URL, and that guard — not
   * this default — is what keeps '' from ever reaching a live request. Only
   * defaults to '' so callers that never reach those calls (preview mode, or
   * a Direct-Debit-only flow, which still resolves its own tenant server-side
   * — see the comment on createDirectDebitFlow in ../api.ts) don't have to
   * pass a value that means nothing to them.
   */
  tenantSlug?: string;
  previewMode?: boolean;
  /**
   * Where the supporter has come back from GoCardless, if they have. The
   * sign-up endpoint sends them to `?dd=complete` or `?dd=cancelled` on this
   * same page; without this they would land on a blank form with no sign their
   * mandate was set up, and reasonably try again.
   */
  directDebitReturn?: 'complete' | 'cancelled';
}

export function FormRenderer({ form, embedKey, tenantSlug = '', previewMode = false, directDebitReturn }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // A supporter returning from a completed mandate has already given; treat that
  // exactly like a successful submission so they get the same thank-you (or
  // redirect) as everybody else.
  const [status, setStatus] = useState<Status>(directDebitReturn === 'complete' ? 'success' : 'idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [intent, setIntent] = useState<PaymentIntentResult | null>(null);
  // Direct Debit failures stay ON the form rather than replacing it: the whole
  // point of the message is that the supporter can pick the other payment
  // method, which needs the form they filled in to still be there.
  const [directDebitError, setDirectDebitError] = useState('');

  // `name` is the charity's internal label; `title` is what the supporter sees.
  // Forms created before titles existed have no title, so fall back to `name`.
  const heading = form.title?.trim() || form.name;

  const paymentField = form.fields.find((f) => f.type === 'payment');
  // Stripe config is only needed by the card path. A form offering Direct Debit
  // alone must still render for a charity that has never connected Stripe —
  // gating on `paymentField` alone turned every such form into an error screen.
  const needsStripe = paymentField?.payment ? resolveOfferedMethods(paymentField.payment).includes('card') : !!paymentField;
  const paymentsUnavailable =
    needsStripe && (form.paymentsUnavailable || !form.publishableKey || !form.stripeAccountId);

  const handleChange = (fieldId: string, value: unknown) =>
    setValues(v => ({ ...v, [fieldId]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validateForm(form.fields, values);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (previewMode) { setStatus('success'); return; }

    if (paymentField) {
      await beginPayment();
      return;
    }

    setStatus('submitting');
    try {
      await submitForm(tenantSlug, form.formId, embedKey, { ...values, __hp: '' });
      setStatus('success');
    } catch (err: unknown) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const beginPayment = async () => {
    const config = paymentField!.payment!;
    const selection = (values[paymentField!.fieldId] as PaymentSelection | undefined) ?? {};
    const amountMinor = config.amountMode === 'fixed' ? config.fixedAmountMinor : selection.amountMinor;
    const frequency = resolveFrequency(config, selection);
    const method = resolveSelectedMethod(config, selection);

    if (config.amountMode === 'donor_choice' && !amountMinor) {
      setErrors((prev) => ({ ...prev, [paymentField!.fieldId]: 'Please choose an amount' }));
      return;
    }

    // A stored form CAN offer Direct Debit alone on a donor-choice field — the
    // save-time rules only refuse Direct Debit on a one-off *field* — leaving a
    // one-off selection with no usable method. Say so here rather than firing a
    // request at an endpoint that will refuse it and blanking the form.
    if (availableMethods(config, selection).length === 0) {
      setDirectDebitError(
        'This form only accepts Direct Debit, which needs a regular gift. Please choose a regular gift to continue.',
      );
      return;
    }

    setDirectDebitError('');
    setStatus('submitting');

    if (method === 'direct_debit') {
      try {
        const flow = await createDirectDebitFlow(form.formId, embedKey, {
          // __hp carried the same way the submit path carries it: this route
          // creates CRM records for an anonymous caller, so it runs the same
          // honeypot check server-side.
          values: { ...values, __hp: '' },
          amountMinor,
          frequency,
        });
        // The submission and the supporter's Contact are both already stored, so
        // handing the browser over is safe: nothing is lost if they never come
        // back, and whether they authorised the mandate is recorded on the
        // Contact's timeline by the webhook or the sweep, not by this page.
        navigateTo(flow.authorisationUrl);
      } catch (err: unknown) {
        setStatus('idle');
        setDirectDebitError(directDebitErrorMessage(err));
      }
      return;
    }

    try {
      const result = await createPaymentIntent(tenantSlug, form.formId, embedKey, {
        values: { ...values },
        amountMinor,
        frequency,
      });
      setIntent(result);
      setStatus('paying');
    } catch (err: unknown) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not start payment. Please try again.');
    }
  };

  const handlePaymentComplete = (outcome: PaymentOutcome) =>
    setStatus(outcome === 'succeeded' ? 'success' : 'processing');

  useEffect(() => {
    // Never redirect in the builder preview: submitting there sets status to
    // 'success' synchronously, so this would navigate the preview iframe away to
    // the organisation's external URL mid-edit.
    if (previewMode || status !== 'success' || form.postSubmission.type !== 'redirect') return;
    try {
      const target = new URL(form.postSubmission.value);
      if (target.protocol === 'https:') window.location.href = target.href;
    } catch {
      // invalid URL — no redirect
    }
  }, [previewMode, status, form.postSubmission.type, form.postSubmission.value]);

  if (paymentsUnavailable && !previewMode) {
    return (
      <ErrorScreen message="Online payments for this form are temporarily unavailable. Please try again later or contact the organisation." />
    );
  }

  if (status === 'success') {
    if (form.postSubmission.type === 'redirect') {
      // Outside preview the effect above is navigating away, so render nothing.
      // Inside preview there is no navigation, so say what would have happened
      // rather than blanking the pane.
      if (!previewMode) return null;
      return <ThankYouScreen message={`Redirects to ${form.postSubmission.value}`} />;
    }
    return <ThankYouScreen message={form.postSubmission.value} />;
  }
  if (status === 'processing') {
    return <ThankYouScreen message="Thank you — your payment is processing. You'll receive confirmation by email shortly." />;
  }
  if (status === 'error') return (
    <ErrorScreen message={errorMessage}>
      <button className="submit-btn mt-4" onClick={() => setStatus('idle')}>
        Try again
      </button>
    </ErrorScreen>
  );

  if (status === 'paying' && intent) {
    return (
      <div className="form-shell">
        {heading && <h1 className="form-heading">{heading}</h1>}
        <PaymentSection
          clientSecret={intent.clientSecret}
          publishableKey={intent.publishableKey}
          stripeAccountId={intent.stripeAccountId}
          onComplete={handlePaymentComplete}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="form-shell">
      {heading && <h1 className="form-heading">{heading}</h1>}
      {directDebitReturn === 'cancelled' && !directDebitError && (
        <p className="field-error" role="status">
          Your Direct Debit was not set up. You can try again below, or pay another way.
        </p>
      )}
      {form.fields.map(field => {
        const Component = FIELD_COMPONENTS[field.type as keyof typeof FIELD_COMPONENTS];
        if (!Component) return null;
        return (
          <Component key={field.fieldId} field={field}
            value={values[field.fieldId]} onChange={(v: unknown) => handleChange(field.fieldId, v)}
            error={errors[field.fieldId]} />
        );
      })}
      {directDebitError && <p className="field-error" role="alert">{directDebitError}</p>}
      <button type="submit" className="submit-btn" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Please wait…' : paymentField ? 'Continue to payment' : 'Submit'}
      </button>
    </form>
  );
}
